/**
 * _modifiersExtras.ts — Modificadores demo para la categoría Extras.
 *
 * Proteína adicional, sopa, fruta y adiciones al pedido (restaurante).
 */
import type { ModifierGroup } from "@/types";

const g = (
  id: string,
  label: string,
  partial: Omit<ModifierGroup, "id" | "label">,
): ModifierGroup => ({ id, label, ...partial });

const mkComentario = (pfx: string): ModifierGroup =>
  g(`${pfx}_comentario`, "Comentario para preparar", {
    tipoSeleccion: "text",
    required: false,
    placeholderTexto: "Ej: sin hielo, bien caliente…",
    opciones: [],
  });

/** 5 — Fruta de temporada (mismo precio, elige fruta) */
export const MOD_FRUTA_TEMPORADA: ModifierGroup[] = [
  g("ext_fr_fruta", "Fruta", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      {
        id: "ext_fr_papaya",
        label: "Papaya",
        descripcion: "Porción fresca del día",
      },
      {
        id: "ext_fr_melon",
        label: "Melón",
        descripcion: "Porción fresca del día",
      },
      {
        id: "ext_fr_mix",
        label: "Mix papaya y melón",
        descripcion: "Mitad y mitad",
      },
    ],
  }),
  mkComentario("ext_fr"),
];

/** 7 — Proteína adicional ($6.000 base, elige proteína) */
export const MOD_PROTEINA_ADICIONAL: ModifierGroup[] = [
  g("ext_pr_tipo", "Proteína", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "ext_pr_res", label: "Res" },
      { id: "ext_pr_cerdo", label: "Cerdo" },
      { id: "ext_pr_pechuga", label: "Pechuga" },
      { id: "ext_pr_pierna", label: "Pierna" },
      { id: "ext_pr_pernil", label: "Pernil dorado" },
      {
        id: "ext_pr_milanesa",
        label: "Milanesa de pollo apanada",
      },
      {
        id: "ext_pr_chuleta",
        label: "Chuleta valluna apanada",
      },
    ],
  }),
  mkComentario("ext_pr"),
];

/** 8 — Sopa adicional ($7.000, elige sopa) */
export const MOD_SOPA_ADICIONAL: ModifierGroup[] = [
  g("ext_sp_tipo", "Sopa", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "ext_sp_sancocho", label: "Sancocho de pescado" },
      { id: "ext_sp_ajiaco", label: "Ajiaco" },
    ],
  }),
  mkComentario("ext_sp"),
];

/** 9 — Adiciones al pedido (extras opcionales con precio) */
export const MOD_ADICIONES_PEDIDO: ModifierGroup[] = [
  g("ext_ad_extras", "Extras al plato", {
    tipoSeleccion: "multiple",
    required: false,
    min: 0,
    permiteCantidadPorOpcion: true,
    opciones: [
      { id: "ext_ad_icopor", label: "Icópor", precioExtra: 1000 },
      { id: "ext_ad_huevo", label: "Huevo frito", precioExtra: 1500 },
      { id: "ext_ad_ensalada", label: "Ensalada", precioExtra: 3000 },
      { id: "ext_ad_cafe", label: "Café", precioExtra: 3000 },
      { id: "ext_ad_chocolate", label: "Chocolate", precioExtra: 3000 },
      { id: "ext_ad_pan", label: "Pan", precioExtra: 500 },
      { id: "ext_ad_arepa", label: "Arepa", precioExtra: 1000 },
      {
        id: "ext_ad_limonada",
        label: "Limonada de Panela",
        precioExtra: 1000,
      },
      {
        id: "ext_ad_arroz_leche",
        label: "Arroz con Leche",
        precioExtra: 4000,
      },
      {
        id: "ext_ad_agua",
        label: "Botella de agua Cristal",
        precioExtra: 3000,
      },
      {
        id: "ext_ad_arroz",
        label: "Porción de arroz",
        precioExtra: 3000,
      },
      {
        id: "ext_ad_papas",
        label: "Porción de papas a la francesa",
        precioExtra: 4000,
      },
    ],
  }),
  mkComentario("ext_ad"),
];

/** 10 — Principio Adicional ($5.000 base, elige 1 principio) */
export const MOD_PRINCIPIO_ADICIONAL: ModifierGroup[] = [
  g("ext_pi_tipo", "Principio", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "ext_pi_frijol", label: "Frijol" },
      { id: "ext_pi_garbanzo", label: "Garbanzo" },
      { id: "ext_pi_pasta", label: "Pasta" },
    ],
  }),
  mkComentario("ext_pi"),
];

/** 11 — Coca-Cola (bebida extra con presentación por ml) */
export const MOD_COCA_PRESENTACION: ModifierGroup[] = [
  g("ext_coca_presentacion", "Presentación", {
    tipoSeleccion: "single",
    required: true,
    min: 1,
    max: 1,
    opciones: [
      { id: "ext_coca_237", label: "237 ml" },
      { id: "ext_coca_300", label: "300 ml", precioExtra: 500 },
      { id: "ext_coca_15l", label: "1.5 L", precioExtra: 5500 },
    ],
  }),
];

/** Índice por producto cat_extra_0 … cat_extra_17 */
export const EXTRAS_MODIFIERS_BY_INDEX: (ModifierGroup[] | undefined)[] = [
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  MOD_FRUTA_TEMPORADA,
  undefined,
  MOD_PROTEINA_ADICIONAL,
  MOD_SOPA_ADICIONAL,
  MOD_ADICIONES_PEDIDO,
  MOD_PRINCIPIO_ADICIONAL,
  MOD_COCA_PRESENTACION,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
];
